import { useDraftMessage } from "@/hooks/useDraftMessage";
import { useParams, useSearch } from "@tanstack/react-router";
import { useCallback } from "react";
import type { ChatActionType } from "../network/useChatAvailableActions";
import { insertTextAtCursor } from "../utils/insertTextAtCursor";
import { useSendChatMessage } from "./useSendChatMessage";
/**
 * Hook to manage chat message input functionality
 *
 * This hook encapsulates all the logic related to message input,
 * including:
 * - Managing the input field state
 * - Sending typing indicators to other users
 * - Handling message submission
 * - Processing keyboard events (e.g., Enter to send)
 *
 * By extracting this logic into a hook, we keep the UI components
 * simpler and allow for easier testing of the input behavior.
 *
 * @param roomToken - The token identifying the chat room
 * @returns Object containing state and handlers for the chat input
 */
interface UseChatInputProps {
  roomToken: string | undefined;
  chatMode: ChatActionType | undefined;
}

export function useChatInput({ roomToken, chatMode }: UseChatInputProps) {
  // Get patient and encounter IDs from URL
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });

  // Use draft message hook for persistent state
  const {
    draftMessage: messageText,
    setDraftMessage,
    clearDraftMessage,
  } = useDraftMessage("chat", patientId, encounterId?.toString());

  // Get handlers for typing indicators and message sending
  const { handleTyping, sendMessage } = useSendChatMessage({ chatMode });

  /**
   * Handle changes to the input field
   * Updates the input state and sends typing indicators
   */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setDraftMessage(e.target.value);
      if (roomToken) {
        handleTyping(roomToken);
      }
    },
    [handleTyping, roomToken, setDraftMessage],
  );

  /**
   * Handle message sending
   * Validates the message, sends it to the server, and clears the input
   */
  const handleSendMessage = useCallback(() => {
    // Don't send empty messages or if the room token is missing
    if (!roomToken || !messageText.trim()) return;

    sendMessage({
      text: messageText.trim(),
      roomToken,
    });

    // Clear the draft message after sending
    clearDraftMessage();
  }, [messageText, roomToken, sendMessage, clearDraftMessage]);

  /**
   * Handle keyboard events in the input field
   * Allows pressing Enter to send a message (but Shift+Enter for new line)
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  /**
   * Set message text programmatically (e.g., from voice transcription)
   * @param text - The text to insert
   * @param cursorPosition - Optional cursor position to insert at. If not provided, appends to the end
   */
  const setMessageText = useCallback(
    (text: string, cursorPosition?: number) => {
      const newText = insertTextAtCursor(messageText, text, cursorPosition);
      setDraftMessage(newText);
      if (roomToken) {
        handleTyping(roomToken);
      }
    },
    [handleTyping, roomToken, messageText, setDraftMessage],
  );

  /**
   * Handle text change from value (for controlled components like DictationTextarea)
   * Sets the entire text value and triggers typing indicator
   * @param text - The new text value
   */
  const handleTextChange = useCallback(
    (text: string) => {
      setDraftMessage(text);
      if (roomToken) {
        handleTyping(roomToken);
      }
    },
    [handleTyping, roomToken, setDraftMessage],
  );

  return {
    messageText,
    handleInputChange,
    handleSendMessage,
    handleKeyDown,
    setMessageText,
    handleTextChange,
    handleTyping,
  };
}
